import { useState, useRef, useEffect } from "react";
import { api } from "@/lib/api";
import { Robot, Spinner, PaperPlaneRight, Sparkle, Scales, TextT, Notepad, Warning, LightbulbFilament, Copy, Check, X } from "@phosphor-icons/react";
import { toast } from "sonner";

const QUICK_ACTIONS = [
  { key: "melhorar", label: "Melhorar", icon: Sparkle, color: "#F59E0B" },
  { key: "corrigir", label: "Corrigir gramática", icon: TextT, color: "#10B981" },
  { key: "formalizar", label: "Formalizar", icon: Scales, color: "#8B5CF6" },
  { key: "simplificar", label: "Simplificar", icon: LightbulbFilament, color: "#F59E0B" },
  { key: "juridico", label: "Linguagem jurídica", icon: Scales, color: "#8B5CF6" },
  { key: "tecnico", label: "Linguagem técnica", icon: Notepad, color: "#3B82F6" },
  { key: "empresarial", label: "Linguagem empresarial", icon: Notepad, color: "#3B82F6" },
  { key: "reescrever", label: "Reescrever", icon: TextT, color: "#F59E0B" },
  { key: "expandir", label: "Expandir texto", icon: Sparkle, color: "#F59E0B" },
  { key: "resumir", label: "Resumir", icon: Notepad, color: "#3B82F6" },
  { key: "conclusao", label: "Criar conclusão", icon: Sparkle, color: "#10B981" },
  { key: "introducao", label: "Criar introdução", icon: Sparkle, color: "#10B981" },
];

const ANALISES = [
  { key: "inconsistencias", label: "Detectar inconsistências", icon: Warning, color: "#EF4444" },
  { key: "faltantes", label: "Informações faltantes", icon: Warning, color: "#F59E0B" },
];

/**
 * Painel lateral da IA (30% largura), estilo ChatGPT.
 * Props:
 *  - despachoId
 *  - getDocumentoAtual: () => string HTML
 *  - onApplyReplace: (html) => void  — substitui todo o documento
 *  - onApplySelectionReplace: (html) => void — substitui trecho selecionado (se houver)
 *  - selectedText: string
 */
export default function AIAssistantPanel({ despachoId, docType, getDocumentoAtual, onApplyReplace, onApplySelectionReplace, selectedText }) {
  const [messages, setMessages] = useState([
    { role: "system", content: "Olá! Sou seu assistente de edição. Peça revisões, tire dúvidas sobre cláusulas, verifique inconsistências ou use os botões rápidos abaixo para transformar o texto." },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [pendingSuggestion, setPendingSuggestion] = useState(null);
  const scrollRef = useRef(null);

  // Endpoint depende do docType (fallback despacho para retrocompat)
  const endpointBase = docType ? `/editor/${docType}/${despachoId}` : `/despachos/${despachoId}`;

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, pendingSuggestion]);

  const enviarChat = async () => {
    if (!input.trim() || loading) return;
    const pergunta = input.trim();
    setInput("");
    setMessages((m) => [...m, { role: "user", content: pergunta }]);
    setLoading(true);
    try {
      const { data } = await api.post(`${endpointBase}/ai-action`, {
        action: "chat", prompt: pergunta, documento_completo: getDocumentoAtual(),
      });
      setMessages((m) => [...m, { role: "assistant", content: data.resposta }]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro na IA");
      setMessages((m) => [...m, { role: "assistant", content: "❌ Falha ao processar. Tente novamente." }]);
    } finally { setLoading(false); }
  };

  const executarAcao = async (action, label) => {
    setLoading(true);
    setMessages((m) => [...m, { role: "user", content: `⚡ ${label}${selectedText ? " (trecho selecionado)" : ""}` }]);
    try {
      const { data } = await api.post(`${endpointBase}/ai-action`, {
        action,
        texto_selecionado: selectedText || null,
        documento_completo: selectedText ? null : getDocumentoAtual(),
      });
      const isTextReplacement = !["inconsistencias", "faltantes"].includes(action);
      setMessages((m) => [...m, { role: "assistant", content: data.resposta, action, isReplacement: isTextReplacement, hasSelection: !!selectedText }]);
      if (isTextReplacement) {
        setPendingSuggestion({ html: data.resposta, action, label, hasSelection: !!selectedText });
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro na IA");
      setMessages((m) => [...m, { role: "assistant", content: "❌ Falha ao processar." }]);
    } finally { setLoading(false); }
  };

  const aplicarSugestao = () => {
    if (!pendingSuggestion) return;
    if (pendingSuggestion.hasSelection) {
      onApplySelectionReplace(pendingSuggestion.html);
    } else {
      onApplyReplace(pendingSuggestion.html);
    }
    toast.success("Sugestão aplicada");
    setMessages((m) => [...m, { role: "system", content: `✓ ${pendingSuggestion.label} aplicado ao documento.` }]);
    setPendingSuggestion(null);
  };

  const ignorarSugestao = () => {
    setMessages((m) => [...m, { role: "system", content: "✗ Sugestão descartada." }]);
    setPendingSuggestion(null);
  };

  return (
    <div className="tce-card flex flex-col h-full overflow-hidden" data-testid="ai-panel">
      <div className="px-4 py-3 border-b border-[#334155] bg-[#8B5CF6]/10 flex items-center gap-2">
        <div className="w-8 h-8 rounded-md bg-[#8B5CF6]/20 flex items-center justify-center text-[#8B5CF6]">
          <Robot size={16} weight="duotone" />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-[#8B5CF6] font-semibold">IA Assistente</div>
          <div className="text-sm font-medium text-[#E9EEF5]">Claude Sonnet 4.5</div>
        </div>
        {loading && <Spinner size={14} className="animate-spin text-[#8B5CF6] ml-auto" />}
      </div>

      {/* Botões rápidos */}
      <div className="px-3 py-3 border-b border-[#334155] bg-[#0F172A]">
        <div className="text-[10px] uppercase tracking-widest text-[#94A3B8] mb-2">Transformar texto</div>
        {selectedText && (
          <div className="text-[10px] text-[#FCD34D] mb-2 italic">Aplicará apenas ao trecho selecionado ({selectedText.length} caracteres)</div>
        )}
        <div className="flex flex-wrap gap-1.5">
          {QUICK_ACTIONS.map((qa) => (
            <button
              key={qa.key}
              data-testid={`ai-quick-${qa.key}`}
              onClick={() => executarAcao(qa.key, qa.label)}
              disabled={loading}
              className="px-2.5 py-1 rounded-full text-[11px] font-medium border border-[#334155] hover:border-[#8B5CF6] hover:bg-[#8B5CF6]/10 text-[#E9EEF5] disabled:opacity-40 transition-all flex items-center gap-1"
              title={qa.label}
              style={{ color: loading ? undefined : qa.color }}
            >
              <qa.icon size={11} weight="duotone" /> {qa.label}
            </button>
          ))}
        </div>
        <div className="text-[10px] uppercase tracking-widest text-[#94A3B8] mt-3 mb-2">Análises</div>
        <div className="flex flex-wrap gap-1.5">
          {ANALISES.map((qa) => (
            <button
              key={qa.key}
              data-testid={`ai-quick-${qa.key}`}
              onClick={() => executarAcao(qa.key, qa.label)}
              disabled={loading}
              className="px-2.5 py-1 rounded-full text-[11px] font-medium border border-[#334155] hover:border-[#EF4444] hover:bg-[#EF4444]/10 text-[#E9EEF5] disabled:opacity-40 transition-all flex items-center gap-1"
              style={{ color: loading ? undefined : qa.color }}
            >
              <qa.icon size={11} weight="duotone" /> {qa.label}
            </button>
          ))}
        </div>
      </div>

      {/* Chat */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3">
        {messages.map((m, i) => (
          <div key={i} className={`text-sm ${m.role === "user" ? "text-right" : ""}`} data-testid={`ai-msg-${i}`}>
            <div className={`inline-block max-w-[92%] px-3 py-2 rounded-lg ${
              m.role === "user" ? "bg-[#F59E0B] text-[#0F172A]" :
              m.role === "system" ? "bg-[#1E293B] text-[#94A3B8] text-xs italic" :
              "bg-[#0F172A] border border-[#334155] text-[#E9EEF5]"
            }`}>
              {m.role === "assistant" ? (
                <div className="prose prose-sm prose-invert max-w-none ai-response" dangerouslySetInnerHTML={{ __html: m.content }} />
              ) : m.content}
            </div>
          </div>
        ))}
        {pendingSuggestion && (
          <div className="rounded-lg border border-[#F59E0B]/40 bg-[#F59E0B]/5 p-3 space-y-3" data-testid="pending-suggestion">
            <div className="text-[10px] uppercase tracking-widest text-[#F59E0B] font-semibold">Sugestão pendente · {pendingSuggestion.label}</div>
            <div className="text-xs text-[#94A3B8]">
              A IA propõe {pendingSuggestion.hasSelection ? "substituir o trecho selecionado" : "substituir todo o documento"} pelo texto acima. Você aprova?
            </div>
            <div className="flex gap-2">
              <button data-testid="apply-suggestion" onClick={aplicarSugestao}
                      className="flex-1 px-3 py-1.5 rounded-md bg-[#10B981] text-white text-xs font-medium hover:bg-[#059669] flex items-center justify-center gap-1">
                <Check size={12} weight="bold" /> Aplicar
              </button>
              <button data-testid="ignore-suggestion" onClick={ignorarSugestao}
                      className="flex-1 px-3 py-1.5 rounded-md border border-[#334155] text-[#94A3B8] text-xs font-medium hover:bg-[#334155] flex items-center justify-center gap-1">
                <X size={12} weight="bold" /> Ignorar
              </button>
            </div>
          </div>
        )}
        {loading && (
          <div className="text-xs text-[#94A3B8] italic flex items-center gap-2">
            <Spinner size={12} className="animate-spin" /> IA analisando o documento…
          </div>
        )}
      </div>

      {/* Input */}
      <div className="px-3 py-3 border-t border-[#334155] bg-[#0F172A]">
        <div className="flex gap-2">
          <input
            data-testid="ai-chat-input"
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && enviarChat()}
            placeholder="Pergunte à IA sobre o documento…"
            disabled={loading}
            className="tce-input flex-1 px-3 py-2 rounded-md text-sm"
          />
          <button
            data-testid="ai-chat-send"
            onClick={enviarChat}
            disabled={loading || !input.trim()}
            className="p-2 rounded-md bg-[#8B5CF6] text-white hover:bg-[#7C3AED] disabled:opacity-40"
          >
            <PaperPlaneRight size={16} weight="bold" />
          </button>
        </div>
      </div>
    </div>
  );
}
